/**
 * PROFILE STORE
 *
 * Shape (persisted under the key "profile"):
 *   username        string
 *   avatar          string   cosmetic id, "default" until the store sells them
 *   banner          string   cosmetic id
 *   playmat         string   cosmetic id
 *   sleeve          string   cosmetic id
 *   favoriteCard    string | null    card id shown large on PROFILE
 *   favoriteFaction Faction | null
 *   favoriteDeck    string | null    deck id, built-in or saved
 *   tcoin           number   TESTNET COINS balance, BLUEPRINT section 21
 *   stats           { wins, losses, gamesPlayed, rating, highestRank }
 *   joinedAt        number   epoch ms, set once on first run
 *
 * `rating` starts at 1000 and `highestRank` is a placeholder string until
 * ranked play exists (BLUEPRINT section 22). The match flow calls
 * `recordResult` once per finished game; nothing else writes stats.
 *
 * Other engineers: STORE spends `tcoin` through `spend`, PROFILE edits the
 * cosmetic ids, ACHIEVEMENTS read `stats`.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Faction } from '@/engine';
import { adapterStorage } from '@/persist';

export interface ProfileStats {
  wins: number;
  losses: number;
  gamesPlayed: number;
  /** Placeholder ladder rating. Ranked play is not in Phase One. */
  rating: number;
  highestRank: string;
}

export interface ProfileState {
  username: string;
  avatar: string;
  banner: string;
  playmat: string;
  sleeve: string;
  favoriteCard: string | null;
  favoriteFaction: Faction | null;
  favoriteDeck: string | null;
  tcoin: number;
  stats: ProfileStats;
  joinedAt: number;
  /**
   * Cosmetic ids the player has bought. Milestone 8. The free starter of each
   * kind is not listed: `src/store/cosmetics.ts` marks it `default` and it is
   * always available.
   */
  ownedCosmetics: string[];
}

export interface ProfileActions {
  set<K extends keyof ProfileState>(key: K, value: ProfileState[K]): void;
  /** Record a finished match. `draw` counts as a game with no win or loss. */
  recordResult(result: 'win' | 'loss' | 'draw'): void;
  /** Add tCOIN. The dev button in STORE uses this. */
  credit(amount: number): void;
  /** Spend tCOIN. Returns false and changes nothing when the balance is short. */
  spend(amount: number): boolean;
  /** Record a bought cosmetic. Buying one twice is a no-op. */
  addCosmetic(id: string): void;
  ownsCosmetic(id: string): boolean;
  reset(): void;
}

export const STARTING_TCOIN = 5000;

export const DEFAULT_STATS: ProfileStats = {
  wins: 0,
  losses: 0,
  gamesPlayed: 0,
  rating: 1000,
  highestRank: 'Unranked',
};

export function defaultProfile(): ProfileState {
  return {
    username: 'Operator',
    avatar: 'default',
    banner: 'default',
    playmat: 'default',
    sleeve: 'default',
    favoriteCard: null,
    favoriteFaction: null,
    favoriteDeck: null,
    tcoin: STARTING_TCOIN,
    stats: { ...DEFAULT_STATS },
    joinedAt: Date.now(),
    ownedCosmetics: [],
  };
}

export type ProfileStore = ProfileState & ProfileActions;

export const useProfile = create<ProfileStore>()(
  persist(
    (set, get) => ({
      ...defaultProfile(),
      set(key, value) {
        set({ [key]: value } as Partial<ProfileState>);
      },
      recordResult(result) {
        const s = get().stats;
        set({
          stats: {
            ...s,
            wins: s.wins + (result === 'win' ? 1 : 0),
            losses: s.losses + (result === 'loss' ? 1 : 0),
            gamesPlayed: s.gamesPlayed + 1,
          },
        });
      },
      credit(amount) {
        set({ tcoin: get().tcoin + Math.max(0, Math.round(amount)) });
      },
      spend(amount) {
        const cost = Math.max(0, Math.round(amount));
        if (get().tcoin < cost) return false;
        set({ tcoin: get().tcoin - cost });
        return true;
      },
      addCosmetic(id) {
        const owned = get().ownedCosmetics;
        if (owned.includes(id)) return;
        set({ ownedCosmetics: [...owned, id] });
      },
      ownsCosmetic(id) {
        return get().ownedCosmetics.includes(id);
      },
      reset() {
        set({ ...defaultProfile() });
      },
    }),
    {
      name: 'profile',
      storage: adapterStorage<ProfileStore>(),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ProfileState>;
        return {
          ...current,
          ...p,
          stats: { ...DEFAULT_STATS, ...(p.stats ?? {}) },
          ownedCosmetics: p.ownedCosmetics ?? [],
        };
      },
    },
  ),
);

export function winRate(stats: ProfileStats): number {
  const decided = stats.wins + stats.losses;
  return decided === 0 ? 0 : stats.wins / decided;
}
